import { useParams as useQueryParams } from '@/hooks/useParams'
import { useEffect, useState } from 'react'
import { ParsedEarningsAndOrdersData } from '../types/Sale'
import { SetURLSearchParams } from 'react-router-dom'
import { getEarningsAndOrders } from '../repository/getEarningsAndOrders'
import { minDate } from '../functions/minDate'
import { getSales } from '../repository/getSales'

export type Graphs = 'Earnings' | 'Items Sold'

const useSaleGraph = (id?: string) => {
  const { params, setSearchParams } = useQueryParams()

  const [selectedGraph, setSelectedGraph] = useState<Graphs>('Earnings')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [graphData, setGraphData] =
    useState<ParsedEarningsAndOrdersData | null>()
  const [loading, setLoading] = useState(true)

  const fetchEarningsAndOrders = async (startDate: string, endDate: string) => {
    setLoading(true)

    const earningsAndOrders = await getEarningsAndOrders({
      id: id as string,
      startDate,
      endDate
    })

    setStartDate(startDate)
    setEndDate(endDate)
    setGraphData(earningsAndOrders)
    setLoading(false)
  }

  const setInitialDates = (
    startDate: string,
    endDate: string,
    setSearchParams: SetURLSearchParams
  ) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      if (startDate) {
        newParams.set('startDate', startDate)
      }
      if (endDate) {
        newParams.set('endDate', endDate)
      }
      return newParams
    })
  }

  useEffect(() => {
    const initializeGraphData = async () => {
      setLoading(true)
      const parsedSaleData = await getSales(id as string)

      const startD = new Date(parsedSaleData.activeAt)
        .toISOString()
        .split('T')[0]

      const endD = minDate(new Date(parsedSaleData.expiresAt), new Date())

      if (!params.startDate || !params.endDate) {
        setInitialDates(startDate, endDate, setSearchParams)
      }

      await fetchEarningsAndOrders(startD, endD)
      setLoading(false)
    }

    initializeGraphData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return {
    selectedGraph,
    chooseSelectedGraph: setSelectedGraph,
    graphData,
    minStartDate: params.startDate,
    maxEndDate: params.endDate,
    startDate,
    endDate,
    fetchEarningsAndOrders,
    loading
  }
}

export { useSaleGraph }
